import { useContext, useMemo } from 'react';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import { PAX_TYPE } from 'app/typings/core/admin/enum/paxType';
import { AVAILABILITY_MODE } from 'app/typings/core/admin/experiences/enum/availabilityMode';
import type {
  ExtranetAlwaysIncludedOvernightExperience,
  ExtranetPackageItem,
} from 'app/typings/core/extranet/packages/packages';

import { formatExperienceName } from '../../../ExtranetInventory/Modern/_components/TabViews/tabViewUtils';
import type {
  Experience,
  PackExperiencesResult,
} from '../ExtranetPackages.types';
import { extractPrices } from '../_utils/packContent.utils';

export const usePackExperiences = (
  selectedPack?: ExtranetPackageItem,
  alwaysIncludedXps?: ExtranetAlwaysIncludedOvernightExperience[]
): PackExperiencesResult => {
  const { data: experienceCategories } = useExperiencesCategories();
  const { getExperience, getOriginal } = useContext(ModernHotelContext);

  return useMemo(() => {
    const result: PackExperiencesResult = {
      alwaysIncludedXps: [],
      includedExperience: [],
    };

    if (!selectedPack) {
      return result;
    }

    // Process package experiences
    selectedPack.experiences.forEach((pe) => {
      const exp =
        getExperience(pe.experienceId) ?? getOriginal(pe.experienceId);

      if (!exp || !pe.isIncluded) {
        return;
      }

      const category = experienceCategories?.find(
        (cat) => cat.id === exp.categoryId
      )?.name;

      const name = formatExperienceName(exp, category);
      const { adultPrice, childPrice, isDailyPrice } = extractPrices(
        pe.openingConfiguration,
        selectedPack.isDaytime
      );
      const areAdultsAllowed = exp.allowedPaxTypes?.includes(PAX_TYPE.ADULT);
      const areChildrenAllowed = exp.allowedPaxTypes?.includes(PAX_TYPE.CHILD);

      const formattedExp: Experience = {
        id: pe.experienceId.toString(),
        name: name ?? `Experience #${pe.experienceId}`,
        images: pe.coverPicture ? [pe.coverPicture] : undefined,
        adultPrice,
        childPrice,
        areAdultsAllowed,
        areChildrenAllowed,
        isDailyPrice,
        isAlwaysIncludedOvernight: exp.isAlwaysIncludedWithNight,
        configuration: {
          quantity: pe.quantity,
          isPerPerson: pe.isPerPerson,
          isPerNight: pe.isPerNight,
          isOptional: pe.isOptional,
        },
      };

      result.includedExperience.push(formattedExp);
    });

    // Process always included experiences from API response
    if (
      alwaysIncludedXps &&
      alwaysIncludedXps.length > 0 &&
      !selectedPack.isDaytime
    ) {
      alwaysIncludedXps.forEach((alwaysIncludedXp) => {
        const category = experienceCategories?.find(
          (cat) => cat.id === alwaysIncludedXp.categoryId
        )?.name;

        const name = formatExperienceName(
          {
            ...alwaysIncludedXp,
            isAlwaysIncludedWithNight: true,
            allowedPaxTypes: [PAX_TYPE.ADULT, PAX_TYPE.CHILD],
            availabilityMode: AVAILABILITY_MODE.DAILY,
            spaceIds: [],
            isInPublishedPack: false,
          },
          category
        );

        const { adultPrice, childPrice, isDailyPrice } = extractPrices(
          alwaysIncludedXp.openingConfiguration,
          selectedPack.isDaytime
        );

        const formattedExp: Experience = {
          id: alwaysIncludedXp.id.toString(),
          name: name ?? `Experience #${alwaysIncludedXp.id}`,
          images: alwaysIncludedXp.coverPicture
            ? [alwaysIncludedXp.coverPicture]
            : undefined,
          areAdultsAllowed: true,
          areChildrenAllowed: true,
          isAlwaysIncludedOvernight: true,
          adultPrice,
          childPrice,
          isDailyPrice,
          configuration: {
            quantity: 1,
            isPerPerson: true,
            isPerNight: true,
            isOptional: false,
          },
        };

        result.alwaysIncludedXps.push(formattedExp);
      });
    }

    return {
      ...result,
      alwaysIncludedXps: result.alwaysIncludedXps.sort(
        (a, b) => (a.hotelPriority ?? Infinity) - (b.hotelPriority ?? Infinity)
      ),
    };
  }, [
    selectedPack,
    alwaysIncludedXps,
    getExperience,
    getOriginal,
    experienceCategories,
  ]);
};
